<template>
  <b-modal
    ref="modal-edit-bahan-packing"
    dialog-class="!w-full !max-w-[600px]"
    body-class="!p-[32px]"
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    hide-header
  >
    <div v-if="!isSuccess">
      <h3 class="text-[#333333] text-[24px] font-semibold text-center mb-[24px]">
        Edit Data
      </h3>
      <ValidationObserver
        ref="observer"
        v-slot="{ handleSubmit }"
      >
        <form @submit.prevent="handleSubmit(handleEditBahanPacking)">
          <div id="mtbp_form">
            <div class="mb-[24px]">
              <div>
                <div class="relative w-fit pt-[12px] pr-[12px] mb-[8px]">
                  <b-img
                    height="68"
                    width="68"
                    :src="imagePreview"
                    @error="handleImageError($event)"
                  />
                </div>
              </div>
            </div>
            <div class="mb-[24px]">
              <label
                for="name"
                class="mb-[8px]"
              >Bahan Packing</label>
              <b-input
                v-model="item.name"
                type="text"
                name="name"
                class="w-full"
                placeholder="Nama bahan packing"
                disabled
              />
            </div>
            <div class="mb-[24px]">
              <label
                for="name"
                class="mb-[8px]"
              >Pilih Satuan</label>
              <div class="position-relative">
                <b-select
                  v-model="item.charge_type"
                  disabled
                  name="satuan"
                  :options="satuanOptions"
                  class="w-full custom-select"
                  placeholder="Pilih satuan"
                />
                <feather-icon
                  icon="ChevronDownIcon"
                  size="20"
                  class="position-absolute select-icon"
                />
              </div>
            </div>
            <ValidationProvider
              v-slot="{ errors }"
              name="Harga"
              rules="required|min_value:100"
            >
              <div class="mb-[24px]">
                <label
                  for="name"
                  class="mb-[8px]"
                >Harga</label>
                <b-input
                  v-model="priceFormatted"
                  type="text"
                  name="price"
                  class="w-full"
                  placeholder="Rp 0"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  @input="handlePriceInput"
                  @keypress="validateNumericOnly"
                />
                <div
                  v-if="errors.length > 0"
                  class="invalid-feedback"
                >
                  {{ errors[0] }}
                </div>
              </div>
            </ValidationProvider>
            <div class="flex items-center justify-between gap-2">
              <b-button
                variant="outline-primary"
                class="grow w-full"
                @click="handleCloseModal"
              >
                Kembali
              </b-button>
              <b-button
                variant="primary"
                type="submit"
                class="grow w-full flex items-center justify-center gap-4"
                :disabled="isPending"
              >
                <b-spinner
                  v-if="isPending"
                  small
                />
                {{ isPending ? 'Memproses...' : 'Simpan' }}
              </b-button>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
    <div
      v-else
      class="text-center gap-2 relative"
    >
      <button
        class="absolute top-0 right-0"
        @click="handleCloseModal"
      >
        <span class="k-close text-[38px] text-[#333]" />
      </button>
      <img
        src="https://storage.googleapis.com/komerce/assets/icons/warning.svg"
        alt="success"
        width="100"
        height="100"
        class="mx-auto"
      >
      <h3 class="text-[#333] text-[24px] font-semibold my-[20px]">
        Data Sedang Ditinjau
      </h3>
      <p class="text-[#626262] text-[14px] font-normal mb-0">
        Tim Operasional Kompack sedang memeriksa pengajuan bahan packing terbaru. Proses ini biasanya memakan waktu hingga 1x24 jam. Mohon ditunggu sebentar ya
      </p>
      <b-button
        variant="primary"
        class="w-full mt-[20px]"
        @click="handleKonfirmasi"
      >
        Konfirmasi
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { toastError } from '@/libs/toastification'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, min } from 'vee-validate/dist/rules'

extend('required', {
  ...required,
  message: '{_field_} harus diisi',
})

extend('min', {
  ...min,
  message: '{_field_} minimal {length} karakter',
})

extend('min_value', {
  validate: (value, { min: minValue }) => value >= minValue,
  params: ['min'],
  message: '{_field_} minimal {min}',
})

export default {
  name: 'ModalEditBahanPacking',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      item: null,
      price: null,
      priceFormatted: '',
      isSuccess: false,
      isPending: false,
      priceInputTimeout: null,
    }
  },
  computed: {
    chargeType() {
      if (!this.item) return null

      return Object.keys(this.satuanOptions).find(key => this.satuanOptions[key] === this.item.charge_type)
    },
    imagePreview() {
      if (!this.item.image_url) return 'https://storage.googleapis.com/komerce/assets/svg/default-image.svg'

      return this.item.image_url
    },
  },
  created() {
    this.status = null
    this.satuanOptions = [
      { value: 1, text: 'Qty' },
      { value: 2, text: 'm²' },
    ]
  },
  beforeDestroy() {
    if (this.priceInputTimeout) {
      clearTimeout(this.priceInputTimeout)
      this.priceInputTimeout = null
    }
  },
  methods: {
    show({ item, status }) {
      this.item = item
      this.status = status
      this.price = item.price
      this.priceFormatted = this.formatToIDR(item.price)
      this.isSuccess = false
      this.$refs['modal-edit-bahan-packing'].show()
    },
    handleCloseModal() {
      this.$refs['modal-edit-bahan-packing'].hide()
    },
    handleKonfirmasi() {
      this.$emit('confirmed')
      this.handleCloseModal()
    },
    /**
     * Automatically format price input
     */
    handlePriceInput(value) {
      if (this.priceInputTimeout) {
        clearTimeout(this.priceInputTimeout)
      }

      this.priceInputTimeout = setTimeout(() => {
        const numericValue = value.replace(/\D/g, '')

        const numberValue = parseInt(numericValue, 10) || 0
        this.price = numberValue

        this.priceFormatted = this.formatToIDR(numberValue)
      }, 150) // 150ms debounce
    },
    /**
     * Format price to IDR
     * @param {number} value - Price
     * @returns {string} - Formatted price
     */
    formatToIDR(value) {
      if (!value || value === 0) return ''

      return `Rp ${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
    },
    /**
     * Validate numeric only
     * @param {Event} event - Event
     */
    validateNumericOnly(event) {
      const char = String.fromCharCode(event.keyCode || event.which)
      const numericRegex = /^[0-9]$/
      const controlKeys = /^(Backspace|Delete|Tab|Enter|ArrowLeft|ArrowRight|ArrowUp|ArrowDown)$/

      if (!numericRegex.test(char) && !controlKeys.test(event.key)) {
        event.preventDefault()
      }
    },
    /**
     * Handle edit bahan packing
     * note: if status is pengajuan, use put method
     *       if status is konfirmasi, use post method
     *       submission_type is 2 = Edit Mode
     */
    async handleEditBahanPacking() {
      this.isPending = true

      const {
        name, charge_type, id,
      } = this.item

      let formData = {
        name,
        charge_type,
        packing_id: this.status === 'konfirmasi' ? id : undefined,
        price: this.price,
        submission_type: 2, // 2 = Edit Mode
      }

      try {
        if (this.status === 'pengajuan') {
          await this.$http_new.put(`/komship/api/v1/packing-material-submissions/${id}`, formData)
        } else {
          await this.$http_new.post('/komship/api/v1/packing-material-submissions', formData)
        }
        this.isSuccess = true
      } catch (error) {
        toastError('Gagal mengubah data, silahkan coba lagi')
      } finally {
        this.isPending = false
        formData = null
      }
    },
    handleImageError(event) {
      const { target } = event
      if (target.dataset.fallback) {
        target.style.display = 'none'
        return
      }
      target.dataset.fallback = 'true'
      target.src = 'https://storage.googleapis.com/komerce/assets/svg/default-image.svg'
    },
  },
}
</script>

<style scoped>
#mtbp_form label {
  font-size: 14px;
  font-weight: 400;
  color: #333333;
  margin-bottom: 8px;
}
#mtbp_form label#mtbp_form_image {
  font-size: 16px;
  margin-bottom: 16px;
}

/* Custom Select Styling */
.custom-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: none !important;
  padding-right: 2.5rem !important;
}

.select-icon {
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6c757d;
  z-index: 2;
}

  /* Hide default Bootstrap select arrow */
.custom-select::-ms-expand {
  display: none;
}
</style>
